import { useNavigate, Link } from "react-router-dom";
import { PhysicianProfile } from "../components/PhysicianProfile";
import { useSubscription } from "../hooks/useSubscription";

// ============================================
// ICONS
// ============================================

function IconArrowLeft({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
    </svg>
  );
}

function IconCog({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

// ============================================
// PLAN DISPLAY NAMES
// ============================================

const PLAN_DISPLAY: Record<string, { label: string; color: string; bg: string; border: string }> = {
  free: { label: "Free", color: "text-slate-600", bg: "bg-slate-100", border: "border-slate-200" },
  essential: { label: "Essential", color: "text-teal-700", bg: "bg-teal-50", border: "border-teal-200" },
  pro: { label: "Pro", color: "text-indigo-700", bg: "bg-indigo-50", border: "border-indigo-200" },
};

// ============================================
// MAIN COMPONENT
// ============================================

export function Settings() {
  const navigate = useNavigate();
  const { tier, isPro, isEssential, manageSubscription } = useSubscription();
  const planInfo = PLAN_DISPLAY[tier] || PLAN_DISPLAY.free;
  const isPaid = isEssential || isPro;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/")}
                className="p-2 -ml-2 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <IconArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-100 rounded-lg">
                  <IconCog className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-slate-800">Settings</h1>
                  <p className="text-xs text-slate-500">Manage your account and preferences</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Subscription Section */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
              <h2 className="font-semibold text-slate-800">Subscription</h2>
              <p className="text-sm text-slate-500">Manage your plan and billing</p>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${planInfo.color} ${planInfo.bg} border ${planInfo.border}`}>
                    {planInfo.label}
                  </span>
                  <span className="text-sm text-slate-500">
                    {tier === "free" && "2 PA letters / month"}
                    {tier === "essential" && "$29/month — Unlimited PA letters"}
                    {tier === "pro" && "$49/month — Full suite"}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  {isPaid && (
                    <button
                      onClick={manageSubscription}
                      className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                    >
                      Manage billing
                    </button>
                  )}
                  <Link
                    to="/pricing"
                    className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all ${
                      isPaid
                        ? "text-slate-600 bg-slate-100 hover:bg-slate-200"
                        : "text-white bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 shadow-sm shadow-teal-500/20"
                    }`}
                  >
                    {isPaid ? "Compare plans" : "Upgrade"}
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Physician Profile Section */}
          <PhysicianProfile />

          {/* Placeholder for future settings */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
              <h2 className="font-semibold text-slate-800">More Settings Coming Soon</h2>
              <p className="text-sm text-slate-500">Notification preferences, data export, and more</p>
            </div>
            <div className="p-6 text-center text-slate-400">
              <p className="text-sm">Additional settings will appear here in future updates.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;